import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  LockReset as ResetPasswordIcon,
  Block as DeactivateIcon,
  CheckCircle as ActivateIcon,
  PhonelinkErase as Reset2FAIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { familyGroupService } from '../../services/familyGroupService';
import logger from '../../utils/logger';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'reset-password', 'deactivate', 'activate'

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const auth = getAuth();

      // Get current user's familyCode from /users collection (legacy location for auth data)
      const currentUserDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (!currentUserDoc.exists()) {
        setError('User profile not found');
        setUsers([]);
        setLoading(false);
        return;
      }

      const currentUserData = currentUserDoc.data();
      const familyCode = currentUserData.currentFamily || currentUserData.familyCode;

      if (!familyCode) {
        setError('You must be part of a family to manage users');
        setUsers([]);
        setLoading(false);
        return;
      }

      // Query from nested structure: families/{code}/members/
      const familyRef = doc(db, 'families', familyCode);
      const membersCollection = collection(familyRef, 'members');
      const membersSnapshot = await getDocs(membersCollection);

      const usersData = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        familyCode: familyCode // Add familyCode to each user
      }));

      setUsers(usersData);
    } catch (err) {
      setError('Failed to load users');
      logger.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      // Check if user is currently in ADMIN group
      const userGroups = user.groups || [];
      const isCurrentlyAdmin = userGroups.includes('ADMIN');

      if (isCurrentlyAdmin) {
        // Remove from ADMIN group
        await familyGroupService.removeMemberFromGroup(
          user.familyCode,
          'ADMIN',
          user.id
        );

        // Also update user document isAdmin field
        const userDocRef = doc(db, 'users', user.id);
        await updateDoc(userDocRef, {
          isAdmin: false,
          updatedAt: serverTimestamp()
        });

        // CRITICAL FIX: Also update member document isAdmin field
        // isFamilyAdmin() checks: memberData.groups?.includes('ADMIN') || memberData.isAdmin === true
        // Must update BOTH groups array (done above) AND isAdmin field (done here)
        const memberDocRef = doc(db, 'families', user.familyCode, 'members', user.id);
        await updateDoc(memberDocRef, {
          isAdmin: false,
          updatedAt: serverTimestamp()
        });

        setSuccess(`Admin status removed for ${user.email}`);
      } else {
        // Add to ADMIN group
        await familyGroupService.addMemberToGroup(
          user.familyCode,
          'ADMIN',
          user.id
        );

        // Also update user document isAdmin field
        const userDocRef = doc(db, 'users', user.id);
        await updateDoc(userDocRef, {
          isAdmin: true,
          updatedAt: serverTimestamp()
        });

        // CRITICAL FIX: Also update member document isAdmin field
        // isFamilyAdmin() checks: memberData.groups?.includes('ADMIN') || memberData.isAdmin === true
        // Must update BOTH groups array (done above) AND isAdmin field (done here)
        const memberDocRef = doc(db, 'families', user.familyCode, 'members', user.id);
        await updateDoc(memberDocRef, {
          isAdmin: true,
          updatedAt: serverTimestamp()
        });

        setSuccess(`Admin status granted to ${user.email}`);
      }

      loadUsers();
    } catch (err) {
      setError('Failed to update admin status');
      logger.error('Error updating admin status:', err);
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, user.email);
      setSuccess(`Password reset email sent to ${user.email}`);
      setOpenDialog(false);
    } catch (err) {
      setError('Failed to send password reset email');
      logger.error('Error sending password reset email:', err);
    }
  };

  const handleReset2FA = async (user) => {
    try {
      const fns = getFunctions();
      const resetMFA = httpsCallable(fns, 'adminResetUserMFA');
      const result = await resetMFA({ targetUserId: user.id });
      if (result.data?.factorsRemoved === 0 || result.data?.message?.includes('no 2FA')) {
        setSuccess(`${user.email} had no 2FA enrolled — nothing to reset`);
      } else {
        setSuccess(`2FA reset for ${user.email} (${result.data?.factorsRemoved ?? 'all'} factor(s) removed)`);
      }
      setOpenDialog(false);
    } catch (err) {
      setError(err?.message || 'Failed to reset 2FA');
      logger.error('Error resetting 2FA:', err);
    }
  };

  const handleToggleAccountStatus = async (user) => {
    try {
      const newActiveStatus = !user.isActive;
      const updateData = {
        isActive: newActiveStatus,
        updatedAt: serverTimestamp()
      };

      // Update only nested structure
      await updateDoc(
        doc(db, 'families', user.familyCode, 'members', user.id),
        updateData
      );

      setSuccess(`Account ${newActiveStatus ? 'activated' : 'deactivated'} for ${user.email}`);
      loadUsers();
      setOpenDialog(false);
    } catch (err) {
      setError('Failed to update account status');
      logger.error('Error updating account status:', err);
    }
  };

  const handleOpenDialog = (type, user) => {
    setDialogType(type);
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setDialogType('');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Name</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Family Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Admin</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell sx={{ maxWidth: { xs: 130, sm: 200 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                  {user.displayName || `${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {user.familyCode || (
                    <Chip label="Not Set" color="warning" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    color={user.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip
                    label={(user.groups || []).includes('ADMIN') ? 'Admin' : 'User'}
                    color={(user.groups || []).includes('ADMIN') ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0, flexWrap: 'nowrap' }}>
                    <Tooltip title="Toggle Admin">
                      <IconButton
                        size="small"
                        onClick={() => handleToggleAdmin(user)}
                        color={(user.groups || []).includes('ADMIN') ? 'error' : 'primary'}
                      >
                        <AdminIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('reset-password', user)}
                        color="primary"
                      >
                        <ResetPasswordIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset 2FA / MFA">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('reset-2fa', user)}
                        color="warning"
                      >
                        <Reset2FAIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.isActive ? 'Deactivate Account' : 'Activate Account'}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user.isActive ? 'deactivate' : 'activate', user)}
                        color={user.isActive ? 'error' : 'success'}
                      >
                        {user.isActive ? <DeactivateIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {dialogType === 'reset-password' && 'Reset Password'}
          {dialogType === 'reset-2fa' && 'Reset Two-Factor Authentication'}
          {dialogType === 'deactivate' && 'Deactivate Account'}
          {dialogType === 'activate' && 'Activate Account'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'reset-password' && (
            <Typography>
              Are you sure you want to send a password reset email to {selectedUser?.email}?
            </Typography>
          )}
          {dialogType === 'reset-2fa' && (
            <Typography>
              This will remove all 2FA/MFA factors enrolled for <strong>{selectedUser?.email}</strong>.
              The user will need to re-enroll a new authenticator app or phone number on their next login.
            </Typography>
          )}
          {dialogType === 'deactivate' && (
            <Typography>
              Are you sure you want to deactivate {selectedUser?.email}'s account?
              This will prevent them from logging in.
            </Typography>
          )}
          {dialogType === 'activate' && (
            <Typography>
              Are you sure you want to activate {selectedUser?.email}'s account?
              This will allow them to log in again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={() => {
              if (dialogType === 'reset-password') handleResetPassword(selectedUser);
              else if (dialogType === 'reset-2fa') handleReset2FA(selectedUser);
              else if (dialogType === 'deactivate' || dialogType === 'activate') handleToggleAccountStatus(selectedUser);
            }}
            variant="contained"
            color={dialogType === 'deactivate' ? 'error' : dialogType === 'reset-2fa' ? 'warning' : 'primary'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 